'use client';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { submitProductQuestionAction } from '@/app/actions/productDetails';

/* ── styles injected once ─────────────────────────────────────────────────── */
const CSS = `
@keyframes fq-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes fq-panel-in    { from { opacity: 0; transform: translateY(28px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.fq-input {
    width: 100%; border: 1.5px solid #e2e8f0; border-radius: 10px;
    padding: 10px 13px; font-size: 0.84rem; color: #1e293b;
    background: #fff; outline: none; font-family: inherit;
    resize: none; box-sizing: border-box; transition: border-color 0.15s;
}
.fq-input:focus { border-color: var(--primary-color); }
.fq-input::placeholder { color: #94a3b8; }
.fq-submit {
    width: 100%; padding: 11px 0; border-radius: 10px; border: none;
    background: var(--primary-color); color: #fff;
    font-size: 0.88rem; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: opacity 0.15s;
}
.fq-submit:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ── overlay panel ────────────────────────────────────────────────────────── */
function QueryOverlay({ onClose }) {
    const [question,    setQuestion]    = useState('');
    const [name,        setName]        = useState('');
    const [email,       setEmail]       = useState('');
    const [submitting,  setSubmitting]  = useState(false);
    const [done,        setDone]        = useState(false);
    const [error,       setError]       = useState(null);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    }, [onClose]);

    const getProductId = () => {
        if (typeof window === 'undefined') return null;
        const match = window.location.pathname.match(/^\/([a-f\d]{24})(?:\/|$)/i);
        return match ? match[1] : null;
    };

    const handleSubmit = async () => {
        if (!question.trim()) { setError('Please enter your question.'); return; }
        setSubmitting(true);
        setError(null);
        const productId = getProductId() || '000000000000000000000000';
        const payload = {
            question: question.trim(),
            name:     name.trim()  || undefined,
            email:    email.trim() || undefined,
            clientInfo: {
                userAgent: navigator.userAgent,
                referer:   document.referrer || window.location.href,
                productId,
            },
        };
        const res = await submitProductQuestionAction(productId, payload);
        setSubmitting(false);
        if (res.success) setDone(true);
        else setError(res.error || 'Something went wrong. Please try again.');
    };

    return createPortal(
        <>
            <style>{CSS}</style>

            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 3000,
                    background: 'rgba(0,0,0,0.22)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    animation: 'fq-backdrop-in 0.2s ease',
                }}
            />

            {/* Panel */}
            <div style={{
                position: 'fixed', inset: 0, zIndex: 3001,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
                paddingTop:    'max(1rem, env(safe-area-inset-top, 1rem))',
                paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
                pointerEvents: 'none',
            }}>
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        pointerEvents: 'all',
                        width: '100%', maxWidth: 480,
                        background: '#fff',
                        borderRadius: '22px',
                        boxShadow: '0 24px 80px rgba(11,49,99,0.22), 0 6px 24px rgba(11,49,99,0.12)',
                        overflow: 'hidden',
                        animation: 'fq-panel-in 0.28s cubic-bezier(0.22,1,0.36,1)',
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '1rem 1.25rem 0.9rem',
                        borderBottom: '1px solid #f1f5f9',
                        background: 'linear-gradient(135deg, rgba(11,49,99,0.04) 0%, rgba(11,49,99,0.01) 100%)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <div style={{
                                width: 34, height: 34,
                                borderRadius: '10px',
                                background: 'var(--primary-color)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <i className="pi pi-comments" style={{ color: '#fff', fontSize: '0.85rem' }} />
                            </div>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Have a query?</p>
                                <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>We'll get back to you as soon as possible</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            style={{
                                width: 30, height: 30, borderRadius: '50%',
                                border: '1px solid #e2e8f0', background: '#f8fafc',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#64748b', flexShrink: 0,
                            }}
                        >
                            <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '1.25rem' }}>
                        {done ? (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                gap: '0.75rem', padding: '1.5rem 0',
                            }}>
                                <div style={{
                                    width: 52, height: 52, borderRadius: '14px',
                                    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                                    border: '1px solid #bbf7d0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-check" style={{ color: '#16a34a', fontSize: '1.3rem' }} />
                                </div>
                                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Question submitted!</p>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>
                                    We've received your question and will respond shortly.
                                </p>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    style={{
                                        marginTop: '0.5rem', padding: '9px 24px', borderRadius: '10px',
                                        border: '1.5px solid #e2e8f0', background: '#fff',
                                        fontSize: '0.84rem', fontWeight: 600, color: '#475569', cursor: 'pointer',
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>

                                {/* Question */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                                        Your question <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <textarea
                                        className="fq-input"
                                        rows={3}
                                        placeholder="What would you like to know? e.g. Does this support 5GHz WiFi?"
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        maxLength={1000}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3 }}>
                                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{question.length} / 1000</span>
                                    </div>
                                </div>

                                {/* Name + Email */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                                            Name <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                                        </label>
                                        <input
                                            className="fq-input"
                                            type="text"
                                            placeholder="Raj"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                                            Email <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                                        </label>
                                        <input
                                            className="fq-input"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '0.45rem',
                                        background: '#fff1f2', border: '1px solid #fecdd3',
                                        borderRadius: '8px', padding: '8px 12px',
                                    }}>
                                        <i className="pi pi-exclamation-triangle" style={{ color: '#e11d48', fontSize: '0.75rem' }} />
                                        <span style={{ fontSize: '0.78rem', color: '#9f1239' }}>{error}</span>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="fq-submit"
                                    onClick={handleSubmit}
                                    disabled={submitting || !question.trim()}
                                >
                                    {submitting
                                        ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} /> Submitting…</>
                                        : <><i className="pi pi-send" style={{ fontSize: '0.8rem' }} /> Submit Question</>
                                    }
                                </button>

                                <p style={{ margin: 0, fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
                                    <i className="pi pi-lock" style={{ fontSize: '0.6rem', marginRight: 4 }} />
                                    Your details are confidential and only used to respond to your query.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

/* ── trigger button (used in footer) ─────────────────────────────────────── */
export default function FooterQueryOverlay() {
    const [open,    setOpen]    = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: 'var(--primary-color)', fontSize: '0.65rem', fontWeight: 600,
                    lineHeight: 1,
                }}
            >
                Have a query?
                <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
            </button>

            {mounted && open && <QueryOverlay onClose={() => setOpen(false)} />}
        </>
    );
}
